import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Card } from '@/components/ui/card';
import { Plus, Clock, User, Trash2 } from 'lucide-react';
import { APPOINTMENTS } from '@/constants/testIds';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    therapist_id: '',
    date: '',
    time: '',
    duration_minutes: 60,
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      fetchAppointments(dateStr);
    }
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [patientsRes, therapistsRes] = await Promise.all([
        axios.get(`${API}/patients`, { withCredentials: true }),
        axios.get(`${API}/therapists`, { withCredentials: true }),
      ]);
      setPatients(patientsRes.data || []);
      setTherapists(therapistsRes.data || []);
    } catch (error) {
      console.error('Error fetching data for appointments:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar datos');
      setPatients([]);
      setTherapists([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async (date) => {
    try {
      const { data } = await axios.get(`${API}/appointments?date=${date}`, { withCredentials: true });
      setAppointments(data);
    } catch (error) {
      toast.error('Error al cargar citas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/appointments`, formData, { withCredentials: true });
      toast.success('Cita creada exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchAppointments(formData.date);
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al crear cita');
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      therapist_id: '',
      date: '',
      time: '',
      duration_minutes: 60,
      notes: '',
    });
  };

  const openNewAppointment = () => {
    setFormData({
      ...formData,
      date: format(selectedDate, 'yyyy-MM-dd'),
    });
    setDialogOpen(true);
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta cita?')) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success('Cita eliminada');
      fetchAppointments(format(selectedDate, 'yyyy-MM-dd'));
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar cita');
    }
  };

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => a.time.localeCompare(b.time));
  }, [appointments]);

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Calendario de Turnos
          </h1>
          <p className="text-sm mt-1" style={{ color: '#718096' }}>
            Gestiona las citas y sesiones
          </p>
        </div>
        <Button
          data-testid={APPOINTMENTS.createBtn}
          onClick={openNewAppointment}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Nueva Cita
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6" data-testid={APPOINTMENTS.calendar}>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            locale={es}
            className="rounded-md"
          />
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Citas para {format(selectedDate, 'dd MMMM yyyy', { locale: es })}
          </h2>
          
          {appointments.length === 0 ? (
            <Card className="p-8 text-center">
              <p style={{ color: '#718096' }}>No hay citas programadas para este día</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {sortedAppointments.map((apt) => {
                  const patient = patients.find((p) => p.id === apt.patient_id);
                  const therapist = therapists.find((t) => t.id === apt.therapist_id);
                  return (
                    <Card key={apt.id} className="p-4 hover:-translate-y-0.5 transition-transform duration-200">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4 flex-1">
                          <div
                            className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                            style={{ backgroundColor: '#5A7D6C' }}
                          >
                            {apt.time}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                              {patient?.name || 'Paciente no encontrado'}
                            </h3>
                            <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: '#718096' }}>
                              <User size={14} />
                              <span>{therapist?.name || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: '#718096' }}>
                              <Clock size={14} />
                              <span>{apt.duration_minutes} minutos</span>
                            </div>
                            {apt.notes && (
                              <p className="text-sm mt-2" style={{ color: '#2D3748' }}>
                                {apt.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAppointment(apt.id)}
                          className="hover:bg-red-50"
                          title="Eliminar cita"
                        >
                          <Trash2 size={16} style={{ color: '#F44336' }} />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Cita</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Paciente *</Label>
              <select
                value={formData.patient_id}
                onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                required
                className="w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
              >
                <option value="">Seleccionar paciente...</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Terapeuta *</Label>
              <select
                value={formData.therapist_id}
                onChange={(e) => setFormData({ ...formData, therapist_id: e.target.value })}
                required
                className="w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
              >
                <option value="">Seleccionar terapeuta...</option>
                {therapists.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Hora *</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Duración (minutos) *</Label>
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
            <Button type="submit" data-testid={APPOINTMENTS.saveBtn} className="btn-primary w-full">
              Crear Cita
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
